import { ApplicationRef, Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, fromEvent, interval, merge, race, Subject, timer } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { PwaActivityService } from './pwa-activity.service';

export interface PwaUpdateNotice {
  message: string;
  busy: boolean;
  reloading: boolean;
}

@Injectable({ providedIn: 'root' })
export class PwaUpdateService implements OnDestroy {
  private readonly checkIntervalMs = 3 * 60 * 1000;
  private readonly idleMs = 30 * 1000;
  private readonly reloadAttemptKey = 'kfn-pwa-reload-attempt';
  private readonly destroy$ = new Subject<void>();
  private readonly noticeSubject = new BehaviorSubject<PwaUpdateNotice | null>(null);
  readonly notice$ = this.noticeSubject.asObservable();

  private initialized = false;
  private checking = false;
  private lastCheckAt = 0;
  private lastInteractionAt = Date.now();
  private readyAt = 0;
  private readyHash: string | null = null;
  private unrecoverable = false;
  private reloading = false;
  private automaticReloadBlocked = false;
  private reloadRecoveryTimer?: ReturnType<typeof setTimeout>;
  private readonly editedFields = new Map<HTMLElement, string>();

  constructor(
    private updates: SwUpdate,
    private appRef: ApplicationRef,
    private zone: NgZone,
    private router: Router,
    private activity: PwaActivityService,
  ) {}

  init(): void {
    if (this.initialized || !this.updates.isEnabled) return;
    this.initialized = true;

    this.updates.versionUpdates.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event.type === 'VERSION_READY') {
        this.setReady(event.latestVersion.hash);
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.warn('[PWA] نصب نسخه جدید ناموفق بود؛ بررسی بعداً تکرار می‌شود.', event.error);
      }
    });

    this.updates.unrecoverable.pipe(takeUntil(this.destroy$)).subscribe(event => {
      console.warn('[PWA] نسخه فعلی به بارگذاری مجدد نیاز دارد.', event.reason);
      this.unrecoverable = true;
      this.setReady(this.readyHash || 'unrecoverable');
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.activity.navigating = true;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.activity.navigating = false;
        this.removeDetachedFields();
      }
      this.lastInteractionAt = Date.now();
    });

    // Polling and DOM activity listeners must not prevent Angular from stabilizing.
    this.zone.runOutsideAngular(() => {
      merge(
        fromEvent(window, 'online'),
        fromEvent(window, 'focus'),
        fromEvent(window, 'pageshow'),
        fromEvent(document, 'visibilitychange'),
      ).pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.lastInteractionAt = Date.now();
        void this.checkForUpdate();
        this.considerReload();
      });

      merge(
        fromEvent<Event>(document, 'pointerdown', { capture: true, passive: true }),
        fromEvent<Event>(document, 'keydown', { capture: true }),
        fromEvent<Event>(document, 'scroll', { capture: true, passive: true }),
        fromEvent<Event>(document, 'focusin', { capture: true }),
        fromEvent<Event>(document, 'beforeinput', { capture: true }),
        fromEvent<Event>(document, 'input', { capture: true }),
        fromEvent<Event>(document, 'change', { capture: true }),
      ).pipe(takeUntil(this.destroy$)).subscribe(event => {
        this.lastInteractionAt = Date.now();
        this.rememberField(event);
      });

      // Some existing long-lived connections can keep isStable false indefinitely.
      race(
        this.appRef.isStable.pipe(filter(stable => stable), take(1)),
        timer(10000),
      ).pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
        void this.checkForUpdate();
        interval(this.checkIntervalMs).pipe(takeUntil(this.destroy$))
          .subscribe(() => void this.checkForUpdate());
      });

      interval(1000).pipe(takeUntil(this.destroy$))
        .subscribe(() => this.considerReload());
    });
  }

  hasUnsavedChanges(): boolean {
    this.removeDetachedFields();
    if (document.querySelector('[data-pwa-draft="true"]')) return true;
    if (Array.from(document.querySelectorAll('.ng-dirty')).some(field =>
      !field.closest('[data-pwa-draft-scope][data-pwa-draft="false"]'),
    )) return true;

    for (const [field, initialValue] of this.editedFields) {
      if (field.closest('[data-pwa-draft-scope][data-pwa-draft="false"]')) continue;
      // Angular resets these classes when the owner resets its saved form.
      if (field.classList.contains('ng-pristine')) continue;
      if (this.fieldValue(field) !== initialValue) return true;
    }

    return Array.from(document.querySelectorAll<HTMLInputElement>('input[type="file"]'))
      .some(input => !!input.files?.length);
  }

  requestReload(discardChanges = false): boolean {
    if (!this.readyHash || this.reloading || this.isBusy() || !navigator.onLine) {
      this.publishNotice();
      return false;
    }
    if (!discardChanges && this.hasUnsavedChanges()) return false;

    this.reload();
    return true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.noticeSubject.complete();
    if (this.reloadRecoveryTimer) clearTimeout(this.reloadRecoveryTimer);
    this.editedFields.clear();
  }

  private async checkForUpdate(): Promise<void> {
    if (
      this.checking || this.reloading || !navigator.onLine ||
      document.visibilityState !== 'visible' || Date.now() - this.lastCheckAt < 30000
    ) return;

    this.checking = true;
    this.lastCheckAt = Date.now();
    try {
      await this.updates.checkForUpdate();
    } catch (error) {
      console.warn('[PWA] بررسی نسخه جدید ناموفق بود؛ بررسی بعداً تکرار می‌شود.', error);
    } finally {
      this.checking = false;
    }
  }

  private setReady(hash: string): void {
    if (hash !== this.readyHash) {
      this.readyHash = hash;
      this.readyAt = Date.now();
      this.automaticReloadBlocked = this.hasRecentReloadAttempt(hash);
    }
    this.publishNotice();
  }

  private considerReload(): void {
    if (!this.readyHash || this.reloading || document.visibilityState !== 'visible') return;
    this.publishNotice();

    const editing = document.activeElement instanceof HTMLElement &&
      document.activeElement.matches('input, textarea, select, [contenteditable="true"]');
    const dialogOpen = !!document.querySelector(
      'nb-dialog-container, [role="dialog"], .modal.show, .swal2-container, [data-pwa-confirming="true"]',
    );

    if (
      !this.automaticReloadBlocked && navigator.onLine && !this.isBusy() &&
      !this.activity.recentlyBusy && !editing && !dialogOpen &&
      Date.now() - this.readyAt >= 10000 &&
      Date.now() - this.lastInteractionAt >= this.idleMs && !this.hasUnsavedChanges()
    ) {
      this.reload(true);
    }
  }

  private publishNotice(): void {
    if (!this.readyHash) return;
    let message = 'نسخه جدید آماده است و در زمان مناسب به‌صورت خودکار اعمال می‌شود.';
    if (this.unrecoverable) {
      message = 'برای ادامه کار، برنامه باید به‌روزرسانی شود. ابتدا اطلاعات لازم را ذخیره کنید.';
    }
    if (this.hasUnsavedChanges()) {
      message = 'ورودی‌های تغییرکرده دارید. ابتدا اطلاعات لازم را ذخیره کنید، سپس به‌روزرسانی کنید.';
    }
    if (this.automaticReloadBlocked) {
      message = 'به‌روزرسانی خودکار کامل نشد. برای تلاش دوباره، دکمه به‌روزرسانی را بزنید.';
    }
    if (this.isBusy()) message = 'پس از پایان عملیات جاری، امکان به‌روزرسانی فراهم می‌شود.';
    if (!navigator.onLine) message = 'برای اعمال به‌روزرسانی، ابتدا به اینترنت متصل شوید.';
    if (this.reloading) message = 'در حال به‌روزرسانی برنامه…';

    const notice: PwaUpdateNotice = {
      message,
      busy: this.isBusy() || !navigator.onLine,
      reloading: this.reloading,
    };
    const previous = this.noticeSubject.value;
    if (
      previous?.message !== notice.message || previous?.busy !== notice.busy ||
      previous?.reloading !== notice.reloading
    ) this.zone.run(() => this.noticeSubject.next(notice));
  }

  private reload(automatic = false): void {
    this.automaticReloadBlocked = true;
    try {
      sessionStorage.setItem(this.reloadAttemptKey, JSON.stringify({
        hash: this.readyHash,
        at: Date.now(),
      }));
    } catch {
      // Without a persistent loop guard, only allow an explicit user reload.
      if (automatic) {
        this.publishNotice();
        return;
      }
    }
    this.reloading = true;
    this.publishNotice();
    this.zone.runOutsideAngular(() => {
      this.reloadRecoveryTimer = setTimeout(() => {
        // A beforeunload handler may have cancelled the reload.
        this.reloading = false;
        this.publishNotice();
      }, 5000);
    });
    document.location.reload();
  }

  private isBusy(): boolean {
    return this.activity.busy || !!document.querySelector('[data-pwa-busy="true"]');
  }

  private hasRecentReloadAttempt(hash: string): boolean {
    try {
      const attempt = JSON.parse(sessionStorage.getItem(this.reloadAttemptKey) || 'null');
      return attempt?.hash === hash && Date.now() - attempt.at < 5 * 60 * 1000;
    } catch {
      return true;
    }
  }

  private rememberField(event: Event): void {
    if (!(event.target instanceof HTMLElement)) return;
    const field = event.target.closest<HTMLElement>(
      'input, textarea, select, [contenteditable="true"]',
    );
    if (!field || field.closest('[data-pwa-update-banner]')) return;
    if (!this.editedFields.has(field)) {
      this.removeDetachedFields();
      this.editedFields.set(field, this.fieldValue(field));
    }
  }

  private fieldValue(field: HTMLElement): string {
    if (field instanceof HTMLInputElement) {
      return field.type === 'checkbox' || field.type === 'radio'
        ? String(field.checked) : field.value;
    }
    if (field instanceof HTMLSelectElement) {
      return JSON.stringify(Array.from(field.selectedOptions, option => option.value));
    }
    if (field instanceof HTMLTextAreaElement) return field.value;
    return field.innerHTML;
  }

  private removeDetachedFields(): void {
    for (const field of this.editedFields.keys()) {
      if (!field.isConnected) this.editedFields.delete(field);
    }
  }
}
